/**
 * Bundle and save: the popup of products to add (snippets/ym-bundle-offer.liquid), the cart's
 * savings line that opens it again (<ym-bundle-line>, snippets/ym-bundle-line.liquid, in the cart
 * drawer and on the cart page; cartSavings), the chip under the price with its Details
 * (<ym-bundle-chip>, snippets/ym-bundle-chip.liquid) and the cart state of the "Pairs with" card
 * (<ym-pairs-with>, snippets/ym-pairs-with.liquid).
 *
 * Two offers, both automatic discounts that the bundle-picks job copies onto the products:
 * - same kind (custom.bundle_tiers, data-tiers): products of the same blank ship in one box at the
 *   workshop's extra-item rate, e.g. 2 of a kind 20%, 3 or more 30%;
 * - mix and match (custom.bundle_mix, data-mix): any products of the bundle catalog, e.g. any 2
 *   10%, 3 or more 13%.
 * A cart line gets the better of the two (Shopify gives a line one product discount).
 *
 * - fillBundleOffer() sets the popup up for the cart as it is: cards already in the cart are left
 *   out, each card shows its own price once added (same-kind cards that save more say "Best
 *   value"), and the title is the offer in plain numbers: "Buy 2 mugs, save 20%" (the next tiers
 *   and "Mix with other items to save 10–15%." under it) / "Add another mug to save 20%" (under it
 *   "Any other item saves 15%. You're saving 10% now.").
 * When the same kind saves more, the text names it (data-kind, from the blank: ym_blank.bundle_kind,
 * else its garment), so "save up to 20%" never promises a mix the 20% does not cover (28/10/2026:
 * a mug and an ornament got 10% after "buy 2, save up to 20%"). Without a name: "up to".
 *   assets/ym-product.js uses it after an add and from "See matching items" in Details
 *   (openBundleBrowse).
 */
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {{ min: number, pct: number }[]} Tiers */
/** @typedef {{ same: Tiers, mix: Tiers, kind: string }} Offer */

/**
 * The popup's offers.
 * @param {HTMLElement | null | undefined} dialog
 * @returns {Offer}
 */
export function bundleOffer(dialog) {
  return {
    same: parseTiers(dialog?.dataset.tiers),
    mix: parseTiers(dialog?.dataset.mix),
    kind: dialog?.dataset.kind || '',
  };
}

/** @param {Offer} offer */
export function hasOffer(offer) {
  return offer.same.length > 0 || offer.mix.length > 0;
}

/**
 * custom.bundle_tiers / custom.bundle_mix as JSON (data-tiers, data-mix), by minimum quantity.
 * @param {string | undefined} json
 * @returns {Tiers}
 */
export function parseTiers(json) {
  let tiers = [];
  try {
    tiers = JSON.parse(json || '[]');
  } catch {}
  return (Array.isArray(tiers) ? tiers : [])
    .map((t) => ({ min: Number(t.min) || 0, pct: Number(t.pct) || 0 }))
    .filter((t) => t.min >= 2 && t.pct > 0)
    .sort((a, b) => a.min - b.min);
}

/**
 * One item's price (cents) after a percentage off, as Shopify charges it: the discount on each item
 * is rounded down to the cent ($19.99 − 20% = $16.00, $19.99 − 15% = $17.00, 3 × $24.95 − 25% =
 * 3 × $18.72 = $56.16; checked against the cart 30/10/2026).
 * @param {number} price @param {number} pct
 */
export function discounted(price, pct) {
  return price - Math.floor((price * pct) / 100 + 1e-9);
}

/** The discount for a set of n items: the best tier n reaches, else 0. */
export function bundlePct(/** @type {Tiers} */ tiers, /** @type {number} */ n) {
  return tiers.reduce((pct, t) => (n >= t.min ? Math.max(pct, t.pct) : pct), 0);
}

/**
 * The discount of a line with `same` items of its kind and `all` items of the bundle catalog in
 * the cart: the better offer.
 * @param {Offer} offer @param {number} same @param {number} all
 */
export function linePct(offer, same, all) {
  return Math.max(bundlePct(offer.same, same), bundlePct(offer.mix, all));
}

/**
 * The best discount a shopper can reach by adding k more items to a cart with `same` items of the
 * product's kind and `all` bundle items: the added items of the same kind, or of any kind.
 * `upTo` is true when the two differ (the price depends on which item is added).
 * @param {Offer} offer @param {number} same @param {number} all @param {number} k
 */
function reachable(offer, same, all, k) {
  const kind = offer.same.length ? linePct(offer, same + k, all + k) : 0;
  const any = bundlePct(offer.mix, all + k);
  return { pct: Math.max(kind, any), kindPct: kind, anyPct: any, upTo: kind > 0 && any > 0 && kind !== any };
}

/**
 * The next step with a bigger discount than now: { need, pct, kindPct, anyPct, upTo }, or null at
 * the best one. anyPct: what k items of any kind give, 0 when that is no more than now.
 * @param {Offer} offer @param {number} same @param {number} all
 */
export function bundleNext(offer, same, all) {
  const current = linePct(offer, same, all);
  const max = Math.max(0, ...offer.same.map((t) => t.min), ...offer.mix.map((t) => t.min));
  for (let k = 1; same + k <= max || all + k <= max; k += 1) {
    const step = reachable(offer, same, all, k);
    if (step.pct > current) return { need: k, ...step, anyPct: step.anyPct > current ? step.anyPct : 0 };
  }
  return null;
}

/** @typedef {{ need: number, pct: number, kindPct: number, anyPct: number, upTo: boolean }} Step */

/**
 * The next step in two parts: { main: "add another mug to save 20%", other: "any other item saves
 * 15%" } when the same kind saves more, else { main: "add 1 more to save 15%", other: '' }.
 * @param {Step} step @param {string} kind
 */
export function nextParts(step, kind) {
  if (!kind || step.kindPct <= step.anyPct) {
    return { main: `add ${step.need} more to save ${step.upTo ? 'up to ' : ''}${step.pct}%`, other: '' };
  }
  const one = step.need === 1;
  return {
    main: `add ${one ? `another ${kind}` : `${step.need} more ${plural(kind)}`} to save ${step.kindPct}%`,
    other: step.anyPct ? `${one ? 'any other item' : `any ${step.need} other items`} saves ${step.anyPct}%` : '',
  };
}

/**
 * "add another mug to save 20%, or any other item to save 15%" / "add 1 more to save 15%"
 * @param {Step} step @param {string} kind
 */
export function nextText(step, kind) {
  const { main, other } = nextParts(step, kind);
  return other ? `${main}, or ${other.replace(' saves ', ' to save ')}` : main;
}

/** "mug" → "mugs" (ym_blank.bundle_kind is a word that takes s) */
function plural(/** @type {string} */ kind) {
  return `${kind}s`;
}

/**
 * @typedef {{ buy: string, named: boolean, mix: boolean, save: string }} OfferStep
 *   buy: "2" / "3 or more" (empty for mix); named: the same kind, named; mix: mix with other items;
 *   save: "20%", "up to 20%", "10–15%".
 */

/**
 * The offer by number of items. When the same kind saves more and has a name: its tiers, named,
 * then mix with other items as one step (2 mugs 20%, 3 or more 25%, mix 10–15%). Otherwise the best
 * of both offers at each count: 2 up to 15%, 3 or more up to 20% ("up to" when the same kind saves
 * more than any mix).
 * @param {Offer} offer
 * @returns {OfferStep[]}
 */
function offerSteps(offer) {
  const tiers = offer.same;
  if (offer.kind && tiers.some((t) => t.pct > bundlePct(offer.mix, t.min))) {
    /** @type {OfferStep[]} */
    const steps = tiers.map((t, i) => ({
      buy: `${t.min}${i === tiers.length - 1 ? ' or more' : ''}`,
      named: true,
      mix: false,
      save: `${t.pct}%`,
    }));
    const pcts = offer.mix.map((t) => t.pct);
    if (pcts.length) {
      const low = Math.min(...pcts);
      const high = Math.max(...pcts);
      steps.push({ buy: '', named: false, mix: true, save: `${low === high ? low : `${low}–${high}`}%` });
    }
    return steps;
  }
  const counts = [...new Set([...offer.same, ...offer.mix].map((t) => t.min))].sort((a, b) => a - b);
  return counts.map((n, i) => {
    const kind = bundlePct(offer.same, n);
    const any = bundlePct(offer.mix, n);
    const upTo = kind > 0 && any > 0 && kind !== any ? 'up to ' : '';
    return { buy: `${n}${i === counts.length - 1 ? ' or more' : ''}`, named: false, mix: false, save: `${upTo}${Math.max(kind, any)}%` };
  });
}

/**
 * The offer as sentences: ["Buy 2 mugs, save 20%", "Buy 3 or more, save 25%", "Mix with other
 * items to save 10–15%"] / ["Buy 2, save up to 15%", "Buy 3 or more, save up to 20%"].
 * @param {Offer} offer
 */
export function offerLines(offer) {
  return offerSteps(offer).map((step, i) =>
    step.mix
      ? `Mix with other items to save ${step.save}`
      : `Buy ${step.buy}${step.named && !i ? ` ${plural(offer.kind)}` : ''}, save ${step.save}`
  );
}

/**
 * The offer as table rows (Details under the price): [{ label: "Buy 2 mugs", value: "Save 20%" },
 * { label: "Buy 3 or more mugs", value: "Save 25%" }, { label: "Mix with other items", value:
 * "Save 10–15%" }] / [{ label: "Buy 2", value: "Save up to 15%" }, …].
 * @param {Offer} offer
 */
export function offerRows(offer) {
  return offerSteps(offer).map((step) => ({
    label: step.mix ? 'Mix with other items' : `Buy ${step.buy}${step.named ? ` ${plural(offer.kind)}` : ''}`,
    value: `Save ${step.save}`,
  }));
}

/** "Buy 2, save up to 15%. Buy 3 or more, save up to 20%." @param {Offer} offer */
export function offerText(offer) {
  return offerLines(offer)
    .map((line) => `${line}.`)
    .join(' ');
}

/**
 * Sets the popup up for the cart; returns the number of cards shown (0: nothing to offer).
 * @param {HTMLDialogElement} dialog
 * @param {{ count: number, mixCount: number, inCart: Set<string>, pending?: boolean, skip?: string }} options
 *   count: items of the product's kind in the cart; mixCount: items of the bundle catalog in the
 *   cart; pending: opened from a product page whose product is not in the cart yet (it would join);
 *   skip: a text link under the title that closes the popup ("No thanks, view cart" after an add;
 *   elsewhere the X is enough).
 */
export function fillBundleOffer(dialog, { count, mixCount, inCart, pending = false, skip }) {
  wire(dialog);
  const offer = bundleOffer(dialog);
  if (!hasOffer(offer)) return 0;
  const same = count + (pending ? 1 : 0);
  const all = mixCount + (pending ? 1 : 0);

  const max = Number(dialog.dataset.max) || 6;
  const format = dialog.dataset.moneyFormat || '${{amount}}';
  const currency = dialog.dataset.currency || 'USD';
  // A card of the same kind saves more than one of another kind: "Best value" on it, only when
  // the popup also shows cards that save less (on every card it would say nothing)
  const anyPct = bundlePct(offer.mix, all + 1);
  /** @type {HTMLElement[]} */
  const tags = [];
  let plain = 0;
  let shown = 0;
  let sameShown = 0;
  for (const item of /** @type {NodeListOf<HTMLElement>} */ (dialog.querySelectorAll('[data-ym-bo-item]'))) {
    // The card's own price once added: its kind counts it, the catalog too
    const pct = item.dataset.same === '1' ? linePct(offer, same + 1, all + 1) : anyPct;
    item.hidden = inCart.has(item.dataset.productId || '') || !pct || shown >= max;
    if (item.hidden) continue;
    shown += 1;
    if (item.dataset.same === '1') sameShown += 1;
    const tag = /** @type {HTMLElement | null} */ (item.querySelector('[data-ym-bo-tag]'));
    const best = item.dataset.same === '1' && anyPct > 0 && pct > anyPct;
    if (tag) tag.hidden = true;
    if (best && tag) tags.push(tag);
    if (!best) plain += 1;
    const set = item.querySelector('[data-ym-bo-set]');
    const price = Number(item.dataset.price);
    if (set) set.textContent = formatMoney(discounted(price, pct), format, currency);
  }
  if (!shown) return 0;
  if (plain) for (const tag of tags) tag.hidden = false;

  // Title "Buy 2, save up to 15%" / "Add 1 more to save up to 15%" / "You're saving 30%", and a
  // short line under it. The cards show the exact price of each.
  const lines = offerLines(offer);
  let title = lines[0] || '';
  let note = lines.slice(1).join('. ');
  if (!pending) {
    const current = linePct(offer, count, mixCount);
    const next = bundleNext(offer, count, mixCount);
    if (next) {
      const { main, other } = nextParts(next, offer.kind);
      // No card of the same kind (none scored high enough, or all in the cart): the title is what
      // the cards give, "Add 1 more to save 15%", and the same kind goes under it
      const [head, aside] = other && !sameShown ? [`add ${next.need} more to save ${next.anyPct}%`, main] : [main, other];
      title = capitalize(head);
      note = [aside && capitalize(aside), current ? `You're saving ${current}% now` : ''].filter(Boolean).join('. ');
    } else {
      title = `You're saving ${current}%`;
      note = 'Each one you add is discounted too';
    }
  }
  const titleEl = dialog.querySelector('[data-ym-bo-title]');
  if (titleEl) titleEl.textContent = title;
  const sub = dialog.querySelector('[data-ym-bo-sub]');
  if (sub) {
    sub.textContent = note ? `${note}.` : '';
    sub.hidden = !note;
  }
  const skipEl = /** @type {HTMLElement | null} */ (dialog.querySelector('[data-ym-bo-skip]'));
  if (skipEl) {
    skipEl.textContent = skip || '';
    skipEl.hidden = !skip;
  }
  const grid = dialog.querySelector('.ym-bo__grid');
  if (grid) grid.scrollTop = 0;
  return shown;
}

/** Close on X, the footer button and a click on the backdrop (it lands on the dialog itself). */
function wire(/** @type {HTMLDialogElement} */ dialog) {
  if (dialog.dataset.ymBoWired) return;
  dialog.dataset.ymBoWired = '1';
  dialog.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === dialog || target.closest('[data-ym-bo-close]')) dialog.close();
  });
}

function capitalize(/** @type {string} */ text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * @typedef {{ offer: Offer, any: boolean, count: number, price: number, subtotal: number, final: number }} CartGroup
 *   A bundle group in the cart: its offers, whether it is in the mix-and-match catalog, its items,
 *   the regular price of one, and its lines' regular and discounted totals (cents).
 */

/** "mug", "mug or ornament", "mug, bodysuit or ornament" @param {string[]} names */
function orList(names) {
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/** "A", "A and B", "A, B and C" @param {string[]} names */
function andList(names) {
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Kinds that share a last word go by it: "round ornament" and "acrylic ornament" in one cart are
 * "ornament" (each saves the same with one more of its own).
 * @param {string[]} kinds
 */
function shortKinds(kinds) {
  const last = (/** @type {string} */ kind) => kind.split(' ').pop() || kind;
  const shared = (/** @type {string} */ kind) => kinds.filter((k) => last(k) === last(kind)).length > 1;
  return [...new Set(kinds.map((kind) => (shared(kind) ? last(kind) : kind)))];
}

/**
 * The cart total (cents) with one more item of group i, as Shopify would discount it: every
 * group's lines again at the discount the new counts reach. An estimate (Shopify rounds each line's
 * share), used only to word a step, never shown as a price.
 * @param {CartGroup[]} groups @param {number} i @param {number} total @param {number} mixCount
 */
export function totalWithOne(groups, i, total, mixCount) {
  const mixAdd = groups[i].any ? 1 : 0;
  return groups.reduce((sum, g, h) => {
    const add = h === i ? 1 : 0;
    const pct = linePct(g.offer, g.count + add, g.any ? mixCount + mixAdd : 0);
    // Per line, not per item: a few cents off at most, which the 50-cent margin covers
    return sum - g.final + discounted(g.subtotal + add * g.price, pct);
  }, total);
}

/**
 * The cart's savings line: { lead, text, bonus, group } (bonus: bold words after the text; group:
 * the index of the group whose picks See items opens, -1 for no link), or null for no line.
 * Amounts come from the cart (saved, pct, titles); the step ahead from the groups' offers, with one
 * more item:
 * - of a kind in the cart: the groups that step up the most are named, largest price first, kinds
 *   that share a last word by it; "on the pair" when the other items keep their discount;
 * - of any other kind: "or any other item to save 15%", when that beats the discount now.
 * Under free shipping (freeShip: the amount in cents, 0 when the bar is not shown or reached), when
 * exactly one kind both steps up and brings the total to the amount + $0.50 (totalWithOne; the 50
 * cents cover Shopify's rounding), that step instead: "Add another mug to save 20% and get free
 * shipping."
 * @param {{ saved: string, pct: number, titles: string[], mixCount: number, groups: CartGroup[], total?: number, freeShip?: number }} cart
 *   saved: the amount saved, formatted, empty when nothing is; total: cart.total_price.
 * @returns {{ lead: string, text: string, bonus: string, group: number } | null}
 */
export function cartSavings({ saved, pct, titles, mixCount, groups, total = 0, freeShip = 0 }) {
  if (!groups.length) return null;
  const lead = `You're saving ${saved}`;
  // Lines with different discounts: name them, nothing more to work out
  if (saved && titles.length > 1) return { lead, text: `with ${andList(titles)}.`, bonus: '', group: -1 };

  const all = (/** @type {CartGroup} */ g, /** @type {number} */ k) => (g.any ? mixCount + k : 0);
  const now = groups.map((g) => linePct(g.offer, g.count, all(g, 0)));
  const nowPct = Math.max(...now);
  // One more of each kind in the cart, and one more of any other kind (it gets the mix offer)
  const up = groups.map((g) => linePct(g.offer, g.count + 1, all(g, 1)));
  const mixGroup = groups.findIndex((g) => g.any && g.offer.mix.length);
  const anyPct = mixGroup < 0 ? 0 : bundlePct(groups[mixGroup].offer.mix, mixCount + 1);
  const best = Math.max(0, ...up.filter((p, i) => p > now[i]));

  let ahead = '';
  let group = -1;
  if (best > anyPct) {
    const named = groups
      .map((g, i) => i)
      .filter((i) => up[i] === best && up[i] > now[i])
      .sort((i, j) => groups[j].price - groups[i].price);
    group = named[0];
    if (named.every((i) => groups[i].offer.kind)) {
      // "on the pair": the added item and the one in the cart step up, the others stay as they are
      const pair =
        groups.length > 1 &&
        named.every(
          (i) =>
            groups[i].count === 1 &&
            groups.every((g, h) => h === i || linePct(g.offer, g.count, all(g, 1)) === now[h])
        );
      ahead = `add another ${orList(shortKinds(named.map((i) => groups[i].offer.kind)))} to save ${best}%`;
      if (pair) ahead += ' on the pair';
      if (anyPct > nowPct) ahead += `, or any other item to save ${anyPct}%`;
    } else {
      ahead = `add 1 more to save up to ${best}%`;
    }
  } else if (anyPct > nowPct) {
    ahead = `add 1 more to save ${anyPct}%`;
    group = mixGroup;
  } else if (groups.length === 1) {
    // No step with one more item (an offer from 3 items on): the fewest items that reach one
    const step = bundleNext(groups[0].offer, groups[0].count, all(groups[0], 0));
    if (step) {
      ahead = nextText(step, groups[0].offer.kind);
      group = 0;
    }
  }

  if (!ahead) return saved ? { lead, text: `with the best bundle price (${pct}%).`, bonus: '', group: -1 } : null;

  // One kind whose next item steps up and reaches free shipping: that step alone
  let bonus = '';
  if (freeShip > 0 && total < freeShip) {
    const reach = groups
      .map((g, i) => i)
      .filter((i) => up[i] > now[i] && groups[i].offer.kind && totalWithOne(groups, i, total, mixCount) >= freeShip + 50);
    if (reach.length === 1) {
      group = reach[0];
      ahead = `add another ${groups[group].offer.kind} to save ${up[group]}%`;
      bonus = 'and get free shipping';
    }
  }
  const end = bonus ? '' : '.';
  if (!saved) return { lead: 'Bundle and save:', text: `${ahead}${end}`, bonus, group };
  return { lead, text: `(${pct}%). ${capitalize(ahead)}${end}`, bonus, group };
}

/**
 * <ym-bundle-line>: the cart's one savings line (snippets/ym-bundle-line.liquid), written from its
 * data when it joins the page (also after each re-render of the cart), and See items, which opens
 * the popup of the named group's picks.
 */
class YmBundleLine extends HTMLElement {
  /** @type {HTMLDialogElement | null} The named group's popup */
  dialog = null;
  /** Items of that group in the cart */
  count = 0;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.write();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get inCart() {
    return new Set((this.dataset.inCart || '').split(',').filter(Boolean));
  }

  write() {
    const els = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-ym-bundle-group]')));
    let titles = [];
    try {
      titles = JSON.parse(this.dataset.titles || '[]');
    } catch {}
    const line = cartSavings({
      saved: this.dataset.saved || '',
      pct: Number(this.dataset.pct) || 0,
      // Liquid gives discount titles HTML-escaped ("Mix &amp; match 15%")
      titles: (Array.isArray(titles) ? titles : []).map(
        (title) => new DOMParser().parseFromString(String(title), 'text/html').documentElement.textContent || ''
      ),
      mixCount: Number(this.dataset.mixCount) || 0,
      total: Number(this.dataset.total) || 0,
      freeShip: Number(this.dataset.freeShip) || 0,
      groups: els.map((el) => ({
        offer: bundleOffer(el),
        any: el.dataset.any === '1',
        count: Number(el.dataset.count) || 0,
        price: Number(el.dataset.price) || 0,
        subtotal: Number(el.dataset.subtotal) || 0,
        final: Number(el.dataset.final) || 0,
      })),
    });
    if (!line) {
      this.hidden = true;
      return;
    }
    const lead = this.querySelector('[data-ym-bundle-lead]');
    if (lead) lead.textContent = line.lead;
    const text = this.querySelector('[data-ym-bundle-text]');
    if (text) {
      text.textContent = line.text;
      if (line.bonus) {
        const bonus = document.createElement('strong');
        bonus.textContent = line.bonus;
        text.append(' ', bonus, '.');
      }
    }

    // See items: the named group's popup, while one of its cards can still be added
    const dialog = line.group < 0 ? null : els[line.group]?.querySelector('[data-ym-bo]');
    this.dialog = dialog instanceof HTMLDialogElement ? dialog : null;
    this.count = Number(els[line.group]?.dataset.count) || 0;
    const inCart = this.inCart;
    const canAdd = Array.from(/** @type {NodeListOf<HTMLElement>} */ (this.dialog?.querySelectorAll('[data-ym-bo-item]') || [])).some(
      (item) => !inCart.has(item.dataset.productId || '')
    );
    const see = /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-bundle-see]'));
    if (see) see.hidden = !canAdd;
    this.hidden = false;
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    if (!(/** @type {HTMLElement} */ (event.target).closest('[data-ym-bundle-see]'))) return;
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    event.preventDefault();
    const shown = fillBundleOffer(dialog, {
      count: this.count || 0,
      mixCount: Number(this.dataset.mixCount) || 0,
      inCart: this.inCart,
    });
    if (shown) dialog.showModal();
  };
}

if (!customElements.get('ym-bundle-line')) customElements.define('ym-bundle-line', YmBundleLine);

/**
 * <ym-bundle-chip>: the chip under the price on the product page (snippets/ym-bundle-chip.liquid)
 * and its Details, the offer step by step (offerRows) from the chip's data-tiers, data-mix and
 * data-kind. Under 768px a bottom sheet (modal: X, a tap on the backdrop, Esc, a swipe down), from
 * 768px a popover under the chip (X, a click outside, Esc). Focus goes to X on open and back to
 * Details on close. "See matching items" closes it; assets/ym-product.js then opens the popup.
 */
class YmBundleChip extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {HTMLElement | null} */
  details = null;
  /** @type {number | null} Where a swipe started (clientY), null when not swiping */
  dragY = null;
  dragDy = 0;
  dragT = 0;
  popover = false;

  connectedCallback() {
    const dialog = this.querySelector('[data-ym-bd]');
    const details = this.querySelector('[data-ym-bundle-details]');
    if (!(dialog instanceof HTMLDialogElement) || !(details instanceof HTMLElement)) return;
    this.dialog = dialog;
    this.details = details;
    this.fillRows();
    details.addEventListener('click', this.onDetails);
    dialog.addEventListener('click', this.onDialogClick);
    dialog.addEventListener('close', this.onClose);
    dialog.addEventListener('touchstart', this.onTouchStart, { passive: true });
    dialog.addEventListener('touchmove', this.onTouchMove, { passive: false });
    dialog.addEventListener('touchend', this.onTouchEnd);
    dialog.addEventListener('touchcancel', this.onTouchEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
  }

  fillRows() {
    const list = this.querySelector('[data-ym-bd-rows]');
    if (!list) return;
    list.replaceChildren(
      ...offerRows(bundleOffer(this)).map(({ label, value }) => {
        const row = document.createElement('li');
        row.className = 'ym-bd__row';
        const labelEl = document.createElement('span');
        labelEl.textContent = label;
        const valueEl = document.createElement('span');
        valueEl.className = 'ym-bd__save';
        valueEl.textContent = value;
        row.append(labelEl, valueEl);
        return row;
      })
    );
  }

  onDetails = () => {
    const dialog = this.dialog;
    if (!dialog || !this.details) return;
    if (dialog.open) {
      dialog.close();
      return;
    }
    this.popover = window.matchMedia('(min-width: 768px)').matches;
    dialog.classList.toggle('ym-bd--popover', this.popover);
    this.details.setAttribute('aria-expanded', 'true');
    if (this.popover) {
      dialog.show();
      document.addEventListener('click', this.onOutside, true);
      document.addEventListener('keydown', this.onKey);
    } else {
      dialog.showModal();
    }
    /** @type {HTMLElement | null} */ (dialog.querySelector('[data-ym-bd-close]'))?.focus();
  };

  /** X and "See matching items" close; on a phone a tap on the backdrop lands on the dialog itself. */
  onDialogClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === this.dialog || target.closest('[data-ym-bd-close]')) this.dialog?.close();
  };

  /** Popover: a click anywhere else closes it (Details toggles it itself). */
  onOutside = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {Node} */ (event.target);
    if (this.dialog?.contains(target) || this.details?.contains(target)) return;
    this.dialog?.close();
  };

  /** Popover: Esc (a modal sheet closes on Esc by itself). */
  onKey = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape' && this.dialog?.open) {
      event.preventDefault();
      this.dialog.close();
    }
  };

  onClose = () => {
    document.removeEventListener('click', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
    this.details?.setAttribute('aria-expanded', 'false');
    if (this.dialog) this.dialog.style.transform = '';
    // Back to Details, unless a click outside put focus somewhere else
    const active = document.activeElement;
    if (!active || active === document.body || this.dialog?.contains(active)) this.details?.focus({ preventScroll: true });
  };

  /* Sheet: a swipe down from the top of its content closes it; a short one springs back. */

  onTouchStart = (/** @type {TouchEvent} */ event) => {
    const dialog = this.dialog;
    if (this.popover || !dialog || dialog.scrollTop > 0) {
      this.dragY = null;
      return;
    }
    this.dragY = event.touches[0].clientY;
    this.dragDy = 0;
    this.dragT = event.timeStamp;
    dialog.style.transition = 'none';
  };

  onTouchMove = (/** @type {TouchEvent} */ event) => {
    if (this.dragY === null || !this.dialog) return;
    const dy = event.touches[0].clientY - this.dragY;
    this.dragDy = Math.max(0, dy);
    if (dy <= 0) {
      this.dialog.style.transform = '';
      return;
    }
    event.preventDefault();
    this.dialog.style.transform = `translateY(${dy}px)`;
  };

  onTouchEnd = (/** @type {TouchEvent} */ event) => {
    const dialog = this.dialog;
    if (this.dragY === null || !dialog) return;
    this.dragY = null;
    dialog.style.transition = '';
    const flick = this.dragDy > 30 && event.timeStamp - this.dragT < 250;
    if (this.dragDy > 80 || flick) dialog.close();
    else dialog.style.transform = '';
  };
}

if (!customElements.get('ym-bundle-chip')) customElements.define('ym-bundle-chip', YmBundleChip);

/**
 * <ym-pairs-with>: the "Pairs with" card on the product page (snippets/ym-pairs-with.liquid), which
 * Liquid draws for the cart the page loaded with. After each change of the cart on the page (the
 * CartLinesUpdateEvent the cart drawer listens to, whose result carries the cart's lines) it is set
 * again for the new cart, without a fetch; only a page restored by Back (bfcache) reads /cart.js.
 * - partner not in the cart: button and "Save 10% when you buy both";
 * - partner in the cart, this product not: "Add this mug to your cart to save 10% on both." (data-pct,
 *   the offer for the two) and "In your cart", no button;
 * - both: "You're saving 10% on the pair." while both carry a discount (the smaller of their two
 *   percentages, from the lines), and "In your cart".
 * assets/ym-product.js counts a click on the button (pairs_with_click).
 */
class YmPairsWith extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onCartLinesUpdate = (/** @type {any} */ event) => {
    (event.promise || Promise.resolve({}))
      .then((/** @type {any} */ result) => {
        const items = result?.detail?.items;
        if (Array.isArray(items)) this.update(items);
        else this.refresh();
      })
      .catch(() => {});
  };

  onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.refresh();
  };

  async refresh() {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      if (Array.isArray(cart.items)) this.update(cart.items);
    } catch {
      // Keep the card as it is; the next change of the cart sets it.
    }
  }

  /** @param {any[]} items The cart's lines, as /cart.js gives them */
  update(items) {
    const lines = (/** @type {string | undefined} */ id) => items.filter((item) => String(item.product_id) === id);
    const partner = lines(this.dataset.partnerId);
    const own = lines(this.dataset.productId);
    const saved = (/** @type {any[]} */ list) => {
      const was = list.reduce((sum, item) => sum + (Number(item.original_line_price) || 0), 0);
      const now = list.reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0);
      return was > 0 ? Math.round(((was - now) * 100) / was) : 0;
    };
    const pct = Number(this.dataset.pct) || 0;
    let state = 'default';
    let note = '';
    if (partner.length && own.length) {
      state = 'both';
      const saving = Math.min(saved(partner), saved(own));
      if (saving > 0) note = `You're saving ${saving}% on the pair.`;
    } else if (partner.length) {
      state = 'partner';
      if (pct) note = `Add this ${this.dataset.type || 'item'} to your cart to save ${pct}% on both.`;
    }
    this.dataset.state = state;
    const show = (/** @type {string} */ selector, /** @type {boolean} */ on) => {
      const el = /** @type {HTMLElement | null} */ (this.querySelector(selector));
      if (el) el.hidden = !on;
    };
    show('[data-ym-pairs-save]', state === 'default');
    show('[data-ym-pairs-in]', state !== 'default');
    show('[data-ym-pairs-btn]', state === 'default');
    const noteEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-ym-pairs-note]'));
    if (noteEl) {
      noteEl.textContent = note;
      noteEl.hidden = !note;
    }
  }
}

if (!customElements.get('ym-pairs-with')) customElements.define('ym-pairs-with', YmPairsWith);
